import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as moment from 'moment';
import { Calendar } from 'react-date-range-updated';
import Popper from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';

import TextField from '../text-field';
import { BASE_DATE_FORMAT } from '../../constants';
import {
	Root,
	CalendarLayout,
	createDatePickerTheme,
} from './styled';


const autoCorrectedDatePipe = createAutoCorrectedDatePipe('dd/mm/yyyy');
const DATE_FORMAT = 'DD.MM.YYYY';
const datePickerTheme = createDatePickerTheme();

const validateDateInput = (value: string): boolean => {
	const pattern = /^\d{0,2}\.\d{0,2}\.\d{0,4}$/g;

	return pattern.test(value);
};

const TextMask = (props) => {
	const { inputRef, ...rest } = props;

  return (
    <MaskedInput
      {...rest}
      ref={(ref) => inputRef(ReactDOM.findDOMNode(ref))}
      mask={[/[0-3]/,/\d/,'.',/(0|1)/,/\d/,'.',/(1|2)/,/\d/,/\d/,/\d/]}
      keepCharPositions
			guide
			pipe={autoCorrectedDatePipe}
    />
  );
}

const MaskedTextField = (props) => {
	const { value, onChange } = props;

	return (
		<TextField
			{...props}
			InputProps={{
				inputComponent: TextMask,
				value,
				onChange,
			}}
		/>
	);
}

const noop = () => {};

export type DatePickerProps = {
	value: string;
	disabled?: boolean;
	readonly?: boolean;
	label?: React.ReactNode;
	fullWidth?: boolean;
	onChange: (value: string) => void;
}

type DatePickerState = {
	isOpen: boolean;
	popoverAnchorEl: HTMLElement;
}

class DatePicker extends React.PureComponent<DatePickerProps, DatePickerState> {
	static defaultProps = {
		value: moment().format(BASE_DATE_FORMAT),
		label: 'Дата',
	};
	state = {
		isOpen: false,
		popoverAnchorEl: null,
	};
	dateRangeRef = React.createRef();

	handleOpen = (e: React.SyntheticEvent<HTMLInputElement>) => {
		const { disabled } = this.props;

		e.preventDefault();
		e.stopPropagation();

		if (disabled) return;

		this.setState({
			isOpen: true,
			popoverAnchorEl: e.currentTarget,
		});
	};

	handleClose = () => this.setState({ isOpen: false, popoverAnchorEl: null })

	handleChange = (date: moment.Moment) => {
		const formattedValue = date.format(BASE_DATE_FORMAT);

		this.props.onChange(formattedValue);
		this.handleClose();
	};

	handleChangeFromInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
		const str = e.currentTarget.value;
		const isValid = validateDateInput(str);
		
		if (isValid) {
			const value = moment(str, DATE_FORMAT).format(BASE_DATE_FORMAT);

			this.props.onChange(value);
		}
	};

	renderCalendar = () => {
		const { value } = this.props;
		const setDate = () => value;

		return (
			<CalendarLayout>
				<Calendar
					key={value}
					date={setDate}
					format={BASE_DATE_FORMAT}
					lang='ru'
					firstDayOfWeek={1}
					onChange={this.handleChange}
					theme={datePickerTheme}
				/>
			</CalendarLayout>
		);
	}

	renderDefaultTrigger = () => {
		const {
			value,
			disabled,
			readonly,
			fullWidth,
			...rest
		} = this.props;

		return (
			<MaskedTextField
				{...rest}
				value={value}
				disabled={disabled}
				readonly={readonly}
				fullWidth={fullWidth}
				onClick={(disabled || readonly) ? noop : this.handleOpen}
				onChange={this.handleChangeFromInput}
			/>
		);
	};

	render() {
		const { fullWidth } = this.props;
		const { isOpen, popoverAnchorEl } = this.state;

		return (
			<Root fullWidth={fullWidth}>
				{this.renderDefaultTrigger()}
				<Popper
					open={isOpen}
					anchorEl={popoverAnchorEl}
					placement='bottom-start'
					style={{ zIndex: 2000 }}
					transition>
					{({ TransitionProps }) => (
						<ClickAwayListener onClickAway={this.handleClose}>
							<Grow {...TransitionProps} in={isOpen} style={{ transformOrigin: '0 0 0', width: 'auto' }}>
								<Paper square elevation={1}>
									{this.renderCalendar()}
								</Paper>
							</Grow>
						</ClickAwayListener>
					)}
				</Popper>
			</Root>
		);
	}
}

export default DatePicker;
